import React, { useState, useEffect } from 'react';
import { auth, createUserWithEmailAndPassword } from './auth-compat';
import { supabase } from './supabase';
import { db, getCollectionName } from './firebase';
import { collection, query, onSnapshot, orderBy, addDoc, getDocs, where, doc, deleteDoc, updateDoc, writeBatch, setDoc, resumo } from './firestore-compat';
import { ROLES as CONFIG_ROLES } from './config/permissions';
import { AVAILABLE_MODULES } from './config/modules';
import { toTitleCase } from './utils/formatters';
import UserCreationWizard from './UserCreationWizard';
import PerfilConfig from './PerfilConfig';
import SpeechConfig from './SpeechConfig';
import CaptacaoConfigPanel from './CaptacaoConfigPanel';
import TabelasHCAdmin from './TabelasHCAdmin';
import TemplatesIW from './carefy/TemplatesIW';
import OnlineUsers from './OnlineUsers';
import ServidorStatus from './components/ServidorStatus';
import PermissoesLiberacao from './components/PermissoesLiberacao';
import ContadorEmails, { consultarEmailsHoje } from './components/ContadorEmails';
import AuditoriaUso from './auditoria/AuditoriaUso';
import { situacaoSenha, OPCOES_VALIDADE, VALIDADE_PADRAO_DIAS, senhaForte } from './utils/senhaPolitica';

// Situação do usuário no INEP, para mostrar na lista e no modal de edição
function situacaoInep(user, inepAdmins) {
  if (!user) return null;
  const email = (user.email || '').toLowerCase();
  if (user.soInep) return { tipo: 'so_inep', rotulo: '🎓 Aluno INEP (só INEP)', cor: '#7c3aed', fundo: '#f5f3ff' };
  if (inepAdmins.some(a => (a.email || '').toLowerCase() === email)) return { tipo: 'admin', rotulo: '🎓 INEP: Admin', cor: '#0369a1', fundo: '#e0f2fe' };
  if (user.permissions?.inep_ensino?.view) return { tipo: 'aluno', rotulo: '🎓 INEP: Aluno', cor: '#0369a1', fundo: '#e0f2fe' };
  return null;
}


// AVAILABLE_MODULES agora vem de ./config/modules.js

const AVAILABLE_ROLES = [
  { id: CONFIG_ROLES.AUDITOR_QUALIDADE, label: 'Auditor Qualidade' },
  { id: CONFIG_ROLES.GESTOR_PRONTOBABY, label: 'Gestor Prontobaby' },
  { id: CONFIG_ROLES.GESTOR_HOMECARE, label: 'Gestor Homecare' },
  { id: CONFIG_ROLES.COORDENADOR, label: 'Coordenador' },
  { id: CONFIG_ROLES.COLABORADOR, label: 'Colaborador' },
  { id: CONFIG_ROLES.DIRETORIA, label: 'Diretoria' },
  { id: CONFIG_ROLES.SECRETARIA, label: 'Secretaria da Diretoria' },
  { id: CONFIG_ROLES.COOPERATIVA, label: 'Cooperativa Parceira' },
  { id: CONFIG_ROLES.ESCALISTA, label: 'Escalista' },
  { id: CONFIG_ROLES.RH, label: 'RH / Avaliador' },
  { id: CONFIG_ROLES.TI, label: 'TI / Acessos' },
  { id: CONFIG_ROLES.ADMIN, label: 'Administrador' }
];

function Settings() {
  const [activeTab, setActiveTab] = useState('users');
  const [currentUser, setCurrentUser] = useState(null);

  // --- ESTADOS: MODO MANUTENÇÃO (Fase 7/8 da migração) ---
  const [manutencaoAtivo, setManutencaoAtivo] = useState(false);
  const [manutencaoMsg, setManutencaoMsg] = useState('');
  const [manutencaoSalvando, setManutencaoSalvando] = useState(false);

  // --- ESTADOS: USUÁRIOS ---
  const [users, setUsers] = useState([]);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserAgendaName, setNewUserAgendaName] = useState('');
  const [newUserEquipe, setNewUserEquipe] = useState('');
  const [newUserSetor, setNewUserSetor] = useState('');
  const [newUserForcePass, setNewUserForcePass] = useState(true);
  const [newUserRoles, setNewUserRoles] = useState([]);
  const [newUserPermissions, setNewUserPermissions] = useState(
    AVAILABLE_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: { view: false, edit: false } }), {})
  );
  const [editUserMode, setEditUserMode] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalUserId, setModalUserId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState('');
  const [emailUpdateMsg, setEmailUpdateMsg] = useState('');
  const [inepAdmins, setInepAdmins] = useState([]);

  // --- ESTADOS: AUDITORES ---
  const [auditors, setAuditors] = useState([]);
  const [newAuditorName, setNewAuditorName] = useState('');
  const [newAuditorColor, setNewAuditorColor] = useState('#000000');
  const [confirmDeleteAuditorId, setConfirmDeleteAuditorId] = useState(null);

  // --- ESTADOS: AUDITORES HOMECARE ---
  const [homecareAuditors, setHomecareAuditors] = useState([]);
  const [newHomecareAuditorName, setNewHomecareAuditorName] = useState('');
  const [newHomecareAuditorColor, setNewHomecareAuditorColor] = useState('#000000');
  const [confirmDeleteHomecareAuditorId, setConfirmDeleteHomecareAuditorId] = useState(null);

  // --- ESTADOS: UNIDADES ---
  const [units, setUnits] = useState([]);
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitColor, setNewUnitColor] = useState('#000000');

  // --- ESTADOS: COOPERATIVAS ---
  const [cooperativas, setCooperativas] = useState([]);
  const [newCooperativaName, setNewCooperativaName] = useState('');

  // --- ESTADOS: EMPRESAS TERCEIRAS ---
  const [empresasTerceiras, setEmpresasTerceiras] = useState([]);
  const [newEmpresaTerceiraName, setNewEmpresaTerceiraName] = useState('');

  // --- ESTADOS: EQUIPES ---
  const [equipes, setEquipes] = useState([]);
  const [newEquipeName, setNewEquipeName] = useState('');
  const [newEquipeColor, setNewEquipeColor] = useState('#000000');
  
  // --- ESTADOS: SETORES (ESTOQUE) ---
  const [setores, setSetores] = useState([]);
  const [newSetorName, setNewSetorName] = useState('');

  // --- ESTADOS: TAREFAS HOMECARE ---
  const [homecareTarefasConfig, setHomecareTarefasConfig] = useState({
    setores: [],
    slas: { urgente_importante: 2, nao_urgente_importante: 24, urgente_nao_importante: 4, nao_urgente_nao_importante: 48 },
    executantes: []
  });
  const [newHomecareSetor, setNewHomecareSetor] = useState('');

  // --- ESTADOS: CAPTAÇÃO SLA ---
  const [captacaoConfig, setCaptacaoConfig] = useState({ sla_padrao: 24, sla_vm: 48 });
  const [savingCaptacaoSla, setSavingCaptacaoSla] = useState(false);

  // --- ESTADOS: ESPECIALIDADES ---
  const ESPECIALIDADES_DEFAULT = ['TÉCNICO DE ENFERMAGEM','ENFERMEIRO','FISIOTERAPEUTA','FONOAUDIOLOGO','NUTRICIONISTA','MEDICO','PSICOLOGO','ASSISTENTE SOCIAL','ODONTOLOGISTA'];
  const [especialidades, setEspecialidades] = useState(ESPECIALIDADES_DEFAULT);
  const [novaEspecialidade, setNovaEspecialidade] = useState('');

  // --- ESTADOS: LIXEIRA CAPTAÇÃO ---
  const [captacaoLixeira, setCaptacaoLixeira] = useState([]);

  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(user => { setCurrentUser(user); });
    const unsubUsers = onSnapshot(query(collection(db, getCollectionName('users')), orderBy('name')), snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubAuditors = onSnapshot(query(collection(db, getCollectionName('auditors_config')), orderBy('name')), snap => setAuditors(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubUnits = onSnapshot(query(collection(db, getCollectionName('units_config')), orderBy('name')), snap => setUnits(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubCooperativas = onSnapshot(query(collection(db, getCollectionName('cooperativas_config')), orderBy('name')), snap => setCooperativas(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubEmpresas = onSnapshot(query(collection(db, getCollectionName('empresas_terceiras_config')), orderBy('name')), snap => setEmpresasTerceiras(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubHomecareAuditors = onSnapshot(query(collection(db, getCollectionName('homecare_auditors_config')), orderBy('name')), snap => setHomecareAuditors(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubEquipes = onSnapshot(query(collection(db, getCollectionName('equipes_config')), orderBy('name')), snap => setEquipes(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubSetores = onSnapshot(query(collection(db, getCollectionName('setores_config')), orderBy('name')), snap => setSetores(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubHomecareTarefas = onSnapshot(doc(db, getCollectionName('config_tarefas_homecare'), 'main'), snap => {
      if (snap.exists()) {
        setHomecareTarefasConfig(snap.data());
      } else {
        setDoc(doc(db, getCollectionName('config_tarefas_homecare'), 'main'), {
          setores: ['Enfermagem', 'Fisioterapia', 'Médico'],
          slas: { urgente_importante: 2, nao_urgente_importante: 24, urgente_nao_importante: 4, nao_urgente_nao_importante: 48 },
          executantes: []
        });
      }
    });
    const unsubCaptacao = onSnapshot(doc(db, getCollectionName('config_captacao'), 'main'), snap => {
      if (snap.exists()) setCaptacaoConfig(prev => ({ ...prev, ...snap.data() }));
    });
    const unsubEspecialidades = onSnapshot(doc(db, getCollectionName('config_especialidades'), 'main'), snap => {
      if (snap.exists() && snap.data().lista?.length) setEspecialidades(snap.data().lista);
    });
    const unsubCaptacaoLixeira = onSnapshot(query(collection(db, getCollectionName('captacao_pacientes')), where('excluido', '==', true), resumo('captacao_resumo')), snap => setCaptacaoLixeira(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubInepAdmins = onSnapshot(collection(db, getCollectionName('inep_admins')), snap => setInepAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {});
    return () => { unsubscribeAuth(); unsubUsers(); unsubAuditors(); unsubUnits(); unsubCooperativas(); unsubEmpresas(); unsubHomecareAuditors(); unsubEquipes(); unsubSetores(); unsubHomecareTarefas(); unsubCaptacao(); unsubEspecialidades(); unsubCaptacaoLixeira(); unsubInepAdmins(); };
  }, []);

  // FUNÇÕES: USUÁRIOS
  const handleInviteUser = async (e) => {
    e.preventDefault();
    setError('');
    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
      setError('Preencha nome, e-mail e senha.');
      return;
    }
    try {
      const usersRef = collection(db, getCollectionName('users'));
      const q = query(usersRef, where('email', '==', newUserEmail.trim().toLowerCase()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) throw new Error('E-mail já cadastrado no Firestore.');
      const userCredential = await createUserWithEmailAndPassword(auth, newUserEmail.trim(), newUserPassword);
      const newUserId = userCredential.user.uid;
      await setDoc(doc(db, getCollectionName('users'), newUserId), {
        name: newUserName.trim(),
        agendaName: newUserAgendaName.trim() || newUserName.trim(),
        email: newUserEmail.trim().toLowerCase(),
        equipe: newUserEquipe.trim(),
        setor: newUserSetor.trim(),
        permissions: newUserPermissions,
        roles: newUserRoles,
        forcePasswordChange: newUserForcePass,
        createdAt: new Date(),
        uid: newUserId
      });
      setNewUserName(''); setNewUserAgendaName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserEquipe(''); setNewUserSetor('');
      setNewUserForcePass(true); setNewUserRoles([]);
      setNewUserPermissions(AVAILABLE_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: { view: false, edit: false } }), {}));
      alert('Usuário criado com sucesso!');
      setShowUserModal(false);
    } catch (err) {
      console.error(err);
      setError(err.code === 'auth/email-already-in-use' ? 'Este e-mail já está em uso no Firebase Auth.' : 'Erro ao criar usuário: ' + err.message);
    }
  };

  // Funções administrativas do servidor (api/), com o login de quem está usando
  const chamarApiAdmin = async (rota, body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const resp = await fetch(rota, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify(body),
    });
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(j.error || 'Erro desconhecido');
    return j;
  };

  // Remover: bloqueia o login (sem isso a pessoa continuaria entrando) e apaga o cadastro
  const handleDeleteUser = async (userId) => {
    const alvo = users.find(u => u.id === userId);
    if (!window.confirm(`Remover ${alvo?.name || 'este usuário'}?\n\nO login será BLOQUEADO e o cadastro sai da lista. Para voltar, cadastre de novo com o mesmo e-mail.`)) return;
    try {
      if (alvo?.email) await chamarApiAdmin('/api/bloquear-usuario', { email: alvo.email, bloquear: true });
      await deleteDoc(doc(db, getCollectionName('users'), userId));
      setShowUserModal(false);
    } catch (err) {
      alert('Não foi possível remover: ' + err.message);
    }
  };

  const handleBloquear = async (alvo, bloquear) => {
    const msg = bloquear
      ? `Bloquear o acesso de ${alvo.name}?\n\nA pessoa não consegue mais entrar (nem por senha, nem por código) e quem estiver com o sistema aberto sai em até 30 segundos.`
      : `Desbloquear o acesso de ${alvo.name}?`;
    if (!window.confirm(msg)) return;
    try {
      await chamarApiAdmin('/api/bloquear-usuario', { email: alvo.email, bloquear });
    } catch (err) {
      alert(`Não foi possível ${bloquear ? 'bloquear' : 'desbloquear'}: ` + err.message);
    }
  };

  // Validade da senha: 'agora' | 30 | 60 | 90 | 120 | 'nunca'
  const handleValidadeSenha = async (alvo, valor) => {
    const senha = { ...(alvo.senha || {}) };
    if (valor === 'agora') {
      const emails = await consultarEmailsHoje().catch(() => null);
      const avisoLimite = emails?.configurado && emails.restantes <= 0
        ? `\n\n⛔ ATENÇÃO: o limite diário de e-mails da Brevo já foi atingido (${emails.enviados} de ${emails.limite}). O código só chegaria amanhã e a pessoa ficaria sem acesso até lá.`
        : '';
      if (!window.confirm(`${alvo.name} vai sair do sistema em até 30 segundos e terá que criar uma senha nova com o código enviado para ${alvo.email}.\n\nConfirme que esse e-mail é real e a pessoa tem acesso a ele.${avisoLimite}`)) return;
      senha.trocarAgora = true;
    } else {
      senha.trocarAgora = false;
      senha.validadeDias = valor === 'nunca' ? null : Number(valor);
    }
    await updateDoc(doc(db, getCollectionName('users'), alvo.id), { senha });
  };

  const handleResetPassword = async (email) => {
    const novaSenha = window.prompt(`Nova senha para ${email}:\n(mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial)`);
    if (!novaSenha) return;
    if (!senhaForte(novaSenha)) { alert('A senha precisa ter pelo menos 8 caracteres, com maiúscula, minúscula, número e caractere especial.'); return; }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch('/api/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, newPassword: novaSenha }),
      });
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error || 'Erro desconhecido');
      alert(`✅ Senha de ${email} redefinida com sucesso!`);
    } catch (err) {
      console.error(err);
      alert('Erro ao redefinir senha: ' + err.message);
    }
  };

  const handleUpdateAgendaName = async (userId, newName) => {
    await updateDoc(doc(db, getCollectionName('users'), userId), { agendaName: newName });
  };

  const handleUpdateEquipe = async (userId, newEquipe) => {
    await updateDoc(doc(db, getCollectionName('users'), userId), { equipe: newEquipe });
  };
  const handleUpdateSetor = async (userId, newSetor) => {
    await updateDoc(doc(db, getCollectionName('users'), userId), { setor: newSetor });
  };
  // Data de início no trabalho: o INEP acompanha os cursos obrigatórios até essa data (aba Acompanhamento)
  const handleUpdateInicioTrabalho = async (userId, data) => {
    await updateDoc(doc(db, getCollectionName('users'), userId), { inicioTrabalho: data || '' });
  };

  // Conta "só INEP" (criada pelo robô do INEP) de alguém que também trabalha no Quality: vira conta comum
  const promoverContaInep = async (email) => {
    if (!email) return;
    if (!window.confirm(`Transformar ${email} em conta do Quality?

A trava do INEP sai e você poderá liberar os módulos. A pessoa precisa sair e entrar de novo para valer.`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/conta-inep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ acao: 'promover', email }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'Erro desconhecido');
      alert('Pronto: agora é uma conta do Quality. Libere os módulos abaixo e peça para a pessoa sair e entrar de novo.');
    } catch (err) {
      alert('Não deu certo: ' + err.message);
    }
  };

  const handleUpdateEmail = async (userId, currentEmail, juntarInep = false) => {
    const trimmed = newEmailValue.trim().toLowerCase();
    if (!trimmed || trimmed === currentEmail) {
      setEditingEmail(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailUpdateMsg('❌ E-mail inválido.');
      return;
    }
    try {
      setEmailUpdateMsg('⏳ Atualizando...');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch('/api/update-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentEmail, newEmail: trimmed, juntarInep }),
      });
      const body = await resp.json();
      // o e-mail novo é de uma conta "só INEP" (robô do INEP) da mesma pessoa: oferece juntar
      if (resp.status === 409 && body.codigo === 'EMAIL_DE_CONTA_INEP' && !juntarInep) {
        setEmailUpdateMsg('');
        if (window.confirm(`O e-mail ${trimmed} é de uma conta "só INEP"${body.nome ? ` (${body.nome})` : ''}, criada automaticamente pelo cadastro de profissional.

Juntar? A conta só INEP é apagada e este e-mail passa para esta conta. Cursos e certificados do INEP feitos com esse e-mail vêm junto.`)) {
          return handleUpdateEmail(userId, currentEmail, true);
        }
        return;
      }
      if (!resp.ok) throw new Error(body.error || 'Erro desconhecido');
      // Atualizar também no banco de dados
      await updateDoc(doc(db, getCollectionName('users'), userId), { email: trimmed });
      setEmailUpdateMsg('✅ E-mail atualizado!');
      setEditingEmail(false);
      setTimeout(() => setEmailUpdateMsg(''), 3000);
    } catch (err) {
      setEmailUpdateMsg('❌ ' + err.message);
    }
  };

  const handleAuthorizeAllNew = () => {
    setNewUserPermissions(AVAILABLE_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: { view: true, edit: true } }), {}));
  };

  const handleAuthorizeAllEdit = async (userId) => {
    const allPerms = AVAILABLE_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: { view: true, edit: true } }), {});
    await updateDoc(doc(db, getCollectionName('users'), userId), { permissions: allPerms });
  };

  const handleToggleRole = (roleId, isNewUser = true, userId = null) => {
    if (isNewUser) {
      setNewUserRoles(prev => prev.includes(roleId) ? prev.filter(r => r !== roleId) : [...prev, roleId]);
    } else if (userId) {
      const user = users.find(u => u.id === userId);
      const currentRoles = user.roles || [];
      const newRoles = currentRoles.includes(roleId) ? currentRoles.filter(r => r !== roleId) : [...currentRoles, roleId];
      updateDoc(doc(db, getCollectionName('users'), userId), { roles: newRoles });
    }
  };

  // FUNÇÕES: AUDITORES
  const handleAddAuditor = async (e) => {
    e.preventDefault();
    if (!newAuditorName.trim()) return;
    await addDoc(collection(db, 'auditors_config'), { name: newAuditorName.trim(), color: newAuditorColor });
    setNewAuditorName('');
  };
  const handleDeleteAuditor = async (id) => { await deleteDoc(doc(db, 'auditors_config', id)); setConfirmDeleteAuditorId(null); };
  const handleLinkAuditorUser = async (auditorId, userId) => {
    const userObj = users.find(u => u.id === userId);
    await updateDoc(doc(db, 'auditors_config', auditorId), {
      linkedUserId: userId || null,
      linkedUserName: userObj?.name || null,
      linkedUserEmail: userObj?.email || null,
    });
  };
  const seedAuditores = async () => {
    const padroes = [{ name: 'Mirelli', color: '#9370DB' }, { name: 'Angela', color: '#DB7093' }, { name: 'Veronica', color: '#8B0000' }, { name: 'Marta', color: '#6A5ACD' }, { name: 'Alexandre', color: '#2F4F4F' }];
    const batch = writeBatch(db);
    padroes.forEach(a => { batch.set(doc(collection(db, 'auditors_config')), a); });
    await batch.commit();
    alert('Auditores padrão restaurados!');
  };

  // FUNÇÕES: AUDITORES HOMECARE
  const handleAddHomecareAuditor = async (e) => {
    e.preventDefault();
    if (!newHomecareAuditorName.trim()) return;
    await addDoc(collection(db, 'homecare_auditors_config'), { name: newHomecareAuditorName.trim(), color: newHomecareAuditorColor });
    setNewHomecareAuditorName('');
  };
  const handleDeleteHomecareAuditor = async (id) => { await deleteDoc(doc(db, 'homecare_auditors_config', id)); setConfirmDeleteHomecareAuditorId(null); };
  const handleLinkHomecareAuditorUser = async (auditorId, userId) => {
    const userObj = users.find(u => u.id === userId);
    await updateDoc(doc(db, 'homecare_auditors_config', auditorId), {
      linkedUserId: userId || null,
      linkedUserName: userObj?.name || null,
      linkedUserEmail: userObj?.email || null,
    });
  };

  // FUNÇÕES: UNIDADES
  const handleAddUnit = async (e) => {
    e.preventDefault();
    if (!newUnitName.trim()) return;
    await addDoc(collection(db, 'units_config'), { name: newUnitName.trim(), color: newUnitColor });
    setNewUnitName('');
  };
  const handleDeleteUnit = async (id) => { if (window.confirm('Excluir unidade?')) await deleteDoc(doc(db, 'units_config', id)); };
  const seedUnidades = async () => {
    const padroes = [{ name: 'Prontobaby', color: '#007bff' }, { name: 'CPL', color: '#fd7e14' }, { name: 'Homecare', color: '#6f42c1' }, { name: 'CPB', color: '#ffc107' }];
    const batch = writeBatch(db);
    padroes.forEach(u => { batch.set(doc(collection(db, 'units_config')), u); });
    await batch.commit();
    alert('Unidades padrão restauradas!');
  };

  // FUNÇÕES: COOPERATIVAS
  const handleAddCooperativa = async (e) => {
    e.preventDefault();
    if (!newCooperativaName.trim()) return;
    await addDoc(collection(db, getCollectionName('cooperativas_config')), { name: newCooperativaName.trim() });
    setNewCooperativaName('');
  };
  const handleDeleteCooperativa = async (id) => { if (window.confirm('Excluir cooperativa?')) await deleteDoc(doc(db, getCollectionName('cooperativas_config'), id)); };
  const seedCooperativas = async () => {
    const padroes = ["ACR SERVIÇOS", "ACTUAL", "COOPERSUL", "EXATAFISIO", "FISIO RIO", "FONOPREST", "INOVA", "MODERNA", "QUERUBIM", "REVIGORE LIFE", "PREVENTE CARE", "VITALLE SAÚDE"];
    const batch = writeBatch(db);
    padroes.forEach(c => { batch.set(doc(collection(db, getCollectionName('cooperativas_config'))), { name: c }); });
    await batch.commit();
    alert('Cooperativas padrão restauradas!');
  };

  // FUNÇÕES: EMPRESAS TERCEIRAS
  const handleAddEmpresaTerceira = async (e) => {
    e.preventDefault();
    if (!newEmpresaTerceiraName.trim()) return;
    await addDoc(collection(db, getCollectionName('empresas_terceiras_config')), { name: newEmpresaTerceiraName.trim() });
    setNewEmpresaTerceiraName('');
  };
  const handleDeleteEmpresaTerceira = async (id) => { if (window.confirm('Excluir empresa terceira?')) await deleteDoc(doc(db, getCollectionName('empresas_terceiras_config'), id)); };
  const seedEmpresasTerceiras = async () => {
    const padroes = ["Dismed", "Humana", "Quality", "Homed", "Outra"];
    const batch = writeBatch(db);
    padroes.forEach(c => { batch.set(doc(collection(db, getCollectionName('empresas_terceiras_config'))), { name: c }); });
    await batch.commit();
    alert('Empresas terceiras padrão restauradas!');
  };

  // FUNÇÕES: EQUIPES
  const handleAddEquipe = async (e) => {
    e.preventDefault();
    if (!newEquipeName.trim()) return;
    await addDoc(collection(db, 'equipes_config'), { name: newEquipeName.trim(), color: newEquipeColor });
    setNewEquipeName('');
  };
  const handleDeleteEquipe = async (id) => { if (window.confirm('Excluir equipe?')) await deleteDoc(doc(db, 'equipes_config', id)); };

  // FUNÇÕES: SETORES
  const handleAddSetor = async (e) => {
    e.preventDefault();
    if (!newSetorName.trim()) return;
    await addDoc(collection(db, 'setores_config'), { name: newSetorName.trim() });
    setNewSetorName('');
  };
  const handleDeleteSetor = async (id) => { if (window.confirm('Excluir setor?')) await deleteDoc(doc(db, 'setores_config', id)); };

  // FUNÇÕES: TAREFAS HOMECARE CONFIG
  const handleSaveHomecareSlas = async () => {
    await setDoc(doc(db, 'config_tarefas_homecare', 'main'), { slas: homecareTarefasConfig.slas }, { merge: true });
    alert('SLAs salvos!');
  };

  const handleSaveCaptacaoSla = async () => {
    setSavingCaptacaoSla(true);
    try {
      await setDoc(doc(db, getCollectionName('config_captacao'), 'main'), captacaoConfig, { merge: true });
      alert('Configurações de SLA da Captação salvas!');
    } finally {
      setSavingCaptacaoSla(false);
    }
  };

  const saveEspecialidades = async (lista) => {
    try {
      await setDoc(doc(db, getCollectionName('config_especialidades'), 'main'), { lista });
      setEspecialidades(lista);
    } catch (err) {
      alert('Erro ao salvar especialidades: ' + err.message);
    }
  };
  const handleAddEspecialidade = async (e) => {
    e.preventDefault();
    const nome = novaEspecialidade.trim().toUpperCase();
    if (!nome) return;
    if (especialidades.includes(nome)) { alert('Especialidade já cadastrada.'); return; }
    await saveEspecialidades([...especialidades, nome].sort());
    setNovaEspecialidade('');
  };
  const handleRemoveEspecialidade = async (nome) => {
    if (!window.confirm(`Remover "${nome}"?`)) return;
    await saveEspecialidades(especialidades.filter(e => e !== nome));
  };
  const handleAddHomecareSetor = async (e) => {
    e.preventDefault();
    if (!newHomecareSetor.trim()) return;
    const newSetores = [...(homecareTarefasConfig.setores || []), newHomecareSetor.trim()];
    await setDoc(doc(db, 'config_tarefas_homecare', 'main'), { setores: newSetores }, { merge: true });
    setNewHomecareSetor('');
  };
  const handleRemoveHomecareSetor = async (setor) => {
    const newSetores = (homecareTarefasConfig.setores || []).filter(s => s !== setor);
    await setDoc(doc(db, 'config_tarefas_homecare', 'main'), { setores: newSetores }, { merge: true });
  };
  const handleToggleHomecareExecutante = async (uid) => {
    let newExecs = [...(homecareTarefasConfig.executantes || [])];
    if (newExecs.includes(uid)) newExecs = newExecs.filter(id => id !== uid);
    else newExecs.push(uid);
    await setDoc(doc(db, 'config_tarefas_homecare', 'main'), { executantes: newExecs }, { merge: true });
  };
  
  // --- MODO MANUTENÇÃO (Fase 7/8 da migração) ---
  useEffect(() => {
    if (activeTab !== 'maintenance') return;
    (async () => {
      const { data } = await supabase
        .from('sistema_manutencao')
        .select('ativo, mensagem')
        .eq('id', 1)
        .maybeSingle();
      if (data) {
        setManutencaoAtivo(!!data.ativo);
        setManutencaoMsg(data.mensagem || '');
      }
    })();
  }, [activeTab]);

  const handleToggleManutencao = async () => {
    const novoStatus = !manutencaoAtivo;
    const confirmMsg = novoStatus
      ? 'Ativar o modo manutenção agora? Todo mundo (menos você) vai ver a tela de bloqueio imediatamente, em até 20 segundos.'
      : 'Desativar o modo manutenção e liberar o sistema pra todo mundo de novo?';
    if (!window.confirm(confirmMsg)) return;

    setManutencaoSalvando(true);
    try {
      const { error } = await supabase
        .from('sistema_manutencao')
        .update({ ativo: novoStatus, mensagem: manutencaoMsg, atualizado_em: new Date().toISOString() })
        .eq('id', 1);
      if (error) throw error;
      setManutencaoAtivo(novoStatus);
    } catch (e) {
      alert('Erro ao atualizar o modo manutenção: ' + e.message);
    } finally {
      setManutencaoSalvando(false);
    }
  };

  // --- FUNÇÕES: MANUTENÇÃO (limpeza de dados) ---
  const handleMigrateNames = async () => {
    if (!window.confirm("Deseja padronizar TODOS os nomes de pacientes e profissionais para 'Título' (Apenas iniciais maiúsculas)?\n\nEsta ação atualizará todo o banco de dados.")) return;
    
    try {
      let countP = 0;
      let countProf = 0;

      // 1. Pacientes
      const pacSnap = await getDocs(collection(db, getCollectionName('escalas_pacientes')));
      const batchP = writeBatch(db);
      pacSnap.docs.forEach(d => {
        const data = d.data();
        if (data.nome) {
          const newName = toTitleCase(data.nome);
          if (newName !== data.nome) {
            batchP.update(d.ref, { nome: newName });
            countP++;
          }
        }
      });
      await batchP.commit();

      // 2. Profissionais
      const profSnap = await getDocs(collection(db, getCollectionName('profissionais_parceiros')));
      const batchProf = writeBatch(db);
      profSnap.docs.forEach(d => {
        const data = d.data();
        if (data.nome) {
          const newName = toTitleCase(data.nome);
          if (newName !== data.nome) {
            batchProf.update(d.ref, { nome: newName });
            countProf++;
          }
        }
      });
      await batchProf.commit();

      alert(`Sucesso!\nPacientes atualizados: ${countP}\nProfissionais atualizados: ${countProf}`);
    } catch (err) {
      console.error(err);
      alert("Erro na migração: " + err.message);
    }
  };

  // Estilos reutilizáveis
  const modalInputStyle = {
    padding: '9px 12px', borderRadius: '6px', border: '1px solid #ddd',
    fontSize: '0.9rem', width: '100%', boxSizing: 'border-box', outline: 'none'
  };
  const outlineBlueBtn = {
    background: 'none', border: '1px solid #007bff', color: '#007bff',
    padding: '5px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600'
  };

  return (
    <div className="settings-page-container" style={{ width: '100%', padding: '0', boxSizing: 'border-box' }}>
      <div className="page-header" style={{ marginBottom: '16px', padding: '20px 24px 0' }}>
        <h2 style={{ color: '#555', margin: 0 }}>Configurações do Sistema</h2>
      </div>

      <div className="settings-tabs" style={{ padding: '0 24px', display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
        {/* Grupo: Acesso */}
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aab', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Acesso</span>
        <button className={activeTab === 'users'   ? 'active' : ''} onClick={() => setActiveTab('users')}>👥 Usuários</button>
        <button className={activeTab === 'perfis'  ? 'active' : ''} onClick={() => setActiveTab('perfis')}>🎭 Perfis</button>
        <button className={activeTab === 'online_users' ? 'active' : ''} onClick={() => setActiveTab('online_users')} style={{ position: 'relative' }}>🟢 Online{activeTab !== 'online_users' && <span style={{ position: 'absolute', top: 2, right: 4, width: 7, height: 7, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />}</button>
        <button className={activeTab === 'auditoria_uso' ? 'active' : ''} onClick={() => setActiveTab('auditoria_uso')}>📋 Auditoria de uso</button>

        {/* Separador */}
        <span style={{ width: 1, height: 22, background: '#dde', margin: '0 6px', display: 'inline-block' }} />

        {/* Grupo: Estrutura */}
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aab', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Estrutura</span>
        <button className={activeTab === 'setores'      ? 'active' : ''} onClick={() => setActiveTab('setores')}>🏢 Setores</button>
        <button className={activeTab === 'equipes'      ? 'active' : ''} onClick={() => setActiveTab('equipes')}>👫 Equipes</button>
        <button className={activeTab === 'units'        ? 'active' : ''} onClick={() => setActiveTab('units')}>🏥 Unidades</button>
        <button className={activeTab === 'cooperativas' ? 'active' : ''} onClick={() => setActiveTab('cooperativas')}>🤝 Cooperativas</button>
        <button className={activeTab === 'empresas'     ? 'active' : ''} onClick={() => setActiveTab('empresas')}>🏢 Empresas Terc.</button>

        {/* Separador */}
        <span style={{ width: 1, height: 22, background: '#dde', margin: '0 6px', display: 'inline-block' }} />

        {/* Grupo: Qualidade */}
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aab', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Qualidade</span>
        <button className={activeTab === 'auditors'         ? 'active' : ''} onClick={() => setActiveTab('auditors')}>⚕️ Auditores Q.</button>
        <button className={activeTab === 'homecare_auditors'? 'active' : ''} onClick={() => setActiveTab('homecare_auditors')}>⚕️ Auditores HC</button>
        <button className={activeTab === 'homecare_tarefas' ? 'active' : ''} onClick={() => setActiveTab('homecare_tarefas')}>📋 Tarefas HC</button>
        <button className={activeTab === 'captacao_config' ? 'active' : ''} onClick={() => setActiveTab('captacao_config')}>⚙️ Captação</button>
        <button className={activeTab === 'captacao_lixeira' ? 'active' : ''} onClick={() => setActiveTab('captacao_lixeira')}>🗑️ Lixeira Captação</button>
        <button className={activeTab === 'especialidades' ? 'active' : ''} onClick={() => setActiveTab('especialidades')}>🩺 Especialidades</button>

        {/* Separador */}
        <span style={{ width: 1, height: 22, background: '#dde', margin: '0 6px', display: 'inline-block' }} />

        <button className={activeTab === 'voz'         ? 'active' : ''} onClick={() => setActiveTab('voz')}>🔊 Voz</button>
        <button className={activeTab === 'maintenance' ? 'active' : ''} onClick={() => setActiveTab('maintenance')}>🛠️ Sistema</button>
        {currentUser?.email === 'alexandrebareaoliveira@gmail.com' && (
          <button className={activeTab === 'servidor' ? 'active' : ''} onClick={() => setActiveTab('servidor')}>🖥️ Servidor</button>
        )}

        {currentUser?.email === 'alexandrebareaoliveira@gmail.com' && (
          <>
            {/* Separador */}
            <span style={{ width: 1, height: 22, background: '#dde', margin: '0 6px', display: 'inline-block' }} />
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#aab', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 2 }}>Homecare</span>
            <button className={activeTab === 'tabelas_hc' ? 'active' : ''} onClick={() => setActiveTab('tabelas_hc')}>📋 Tabelas HC</button>
            <button className={activeTab === 'templates_iw' ? 'active' : ''} onClick={() => setActiveTab('templates_iw')}>🧩 Templates IW</button>
          </>
        )}
      </div>

      <main className="settings-main" style={{ width: '100%', padding: '0' }}>
        {error && <p className="error-message">{error}</p>}

        {/* ============================================================ */}
        {/* ABA: USUÁRIOS E PERMISSÕES                                   */}
        {/* ============================================================ */}
        {activeTab === 'users' && (
          <div className="tab-content" style={{ width: '100%' }}>

            {/* WIZARD DE CRIAÇÃO DE USUÁRIO */}
            {showWizard && (
              <UserCreationWizard
                setores={setores}
                equipes={equipes}
                cooperativas={cooperativas}
                onCreated={() => setShowWizard(false)}
                onClose={() => setShowWizard(false)}
              />
            )}

            {/* MODAL EDITAR USUÁRIO */}
            {showUserModal && (() => {
              const isEditing = !!modalUserId;
              const editUser = isEditing ? users.find(u => u.id === modalUserId) : null;
              return (
                <div style={{
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }} onClick={e => { if (e.target === e.currentTarget) setShowUserModal(false); }}>
                  <div style={{
                    backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '1040px',
                    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                  }}>
                    {/* Header */}
                    <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                      <div>
                        <h3 style={{ margin: 0, color: '#1a1a2e', fontSize: '1.2rem' }}>
                          {isEditing ? `✏️ Editar — ${editUser?.name}` : '➕ Novo Usuário'}
                        </h3>
                        {isEditing && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            {editingEmail ? (
                              <>
                                <input
                                  type="email"
                                  value={newEmailValue}
                                  onChange={e => setNewEmailValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleUpdateEmail(modalUserId, editUser?.email); if (e.key === 'Escape') { setEditingEmail(false); setEmailUpdateMsg(''); } }}
                                  autoFocus
                                  style={{ fontSize: '0.85rem', padding: '3px 8px', border: '1px solid #6366f1', borderRadius: 6, outline: 'none', width: 220 }}
                                  placeholder={editUser?.email}
                                />
                                <button onClick={() => handleUpdateEmail(modalUserId, editUser?.email)} style={{ fontSize: '0.75rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}>✓ Salvar</button>
                                <button onClick={() => { setEditingEmail(false); setEmailUpdateMsg(''); }} style={{ fontSize: '0.75rem', background: '#eee', color: '#555', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer' }}>✕</button>
                              </>
                            ) : (
                              <>
                                <span style={{ fontSize: '0.85rem', color: '#888' }}>{editUser?.email}</span>
                                <button onClick={() => { setNewEmailValue(editUser?.email || ''); setEditingEmail(true); setEmailUpdateMsg(''); }} style={{ fontSize: '0.72rem', background: 'none', border: '1px solid #ddd', borderRadius: 5, padding: '2px 7px', cursor: 'pointer', color: '#6366f1' }}>✏️ editar</button>
                              </>
                            )}
                            {emailUpdateMsg && <span style={{ fontSize: '0.78rem', color: emailUpdateMsg.startsWith('✅') ? '#16a34a' : emailUpdateMsg.startsWith('⏳') ? '#888' : '#dc2626' }}>{emailUpdateMsg}</span>}
                          </div>
                        )}
                      </div>
                      <button onClick={() => { setShowUserModal(false); setEditingEmail(false); setEmailUpdateMsg(''); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#999', lineHeight: 1 }}>✕</button>
                    </div>

                    <div style={{ padding: '24px 28px' }}>
                      {error && <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', color: '#856404', fontSize: '0.9rem' }}>{error}</div>}

                      {/* Situação no INEP */}
                      {isEditing && (() => {
                        const inep = situacaoInep(editUser, inepAdmins);
                        if (inep?.tipo === 'so_inep') {
                          return (
                            <div style={{ background: inep.fundo, border: `1px solid ${inep.cor}40`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: '0.88rem', color: '#4c1d95', lineHeight: 1.5 }}>
                              <strong>🎓 Aluno do INEP — conta só INEP.</strong> Entra apenas no portal do INEP; o banco bloqueia o resto do Quality.
                              Conta criada automaticamente a partir do cadastro aprovado pelo RH{editUser?.tipos?.length ? ` (${editUser.tipos.join(', ')})` : ''}.
                              A pessoa entra por <strong>"Primeiro acesso"</strong> na tela de login, com o e-mail acima.
                              <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <button type="button" onClick={() => promoverContaInep(editUser?.email)}
                                  style={{ background: '#7c3aed', color: '#fff', border: 0, borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                                  🔓 Transformar em conta do Quality
                                </button>
                                <span style={{ fontSize: '0.78rem', color: '#6d28d9' }}>Se esta pessoa também trabalha no Quality: tira a trava do INEP para você liberar os módulos. Se ela já tem outra conta, use "editar" e-mail na conta principal para juntar.</span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: '0.85rem', color: '#64748b' }}>
                            <span style={{ fontWeight: 600 }}>INEP:</span>
                            {inep
                              ? <span style={{ background: inep.fundo, color: inep.cor, padding: '3px 10px', borderRadius: 12, fontWeight: 700, fontSize: '0.8rem' }}>{inep.rotulo.replace('🎓 INEP: ', '🎓 ')}</span>
                              : <span style={{ fontStyle: 'italic' }}>sem acesso (libere "INEP — Instituto de Educação e Pesquisa" abaixo para virar aluno)</span>}
                          </div>
                        );
                      })()}

                      {/* Campos somente para novo usuário */}
                      {!isEditing && (
                        <>
                          <h4 style={{ margin: '0 0 14px', color: '#444', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dados de Acesso</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                            <input style={modalInputStyle} type="text" placeholder="Nome completo *" value={newUserName} onChange={e => setNewUserName(e.target.value)} />
                            <input style={modalInputStyle} type="text" placeholder="Nome na Agenda (Ex: Dr. Fulano)" value={newUserAgendaName} onChange={e => setNewUserAgendaName(e.target.value)} />
                            <input style={modalInputStyle} type="email" placeholder="E-mail *" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} />
                            <input style={modalInputStyle} type="password" placeholder="Senha inicial (mín. 6 dígitos) *" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} />
                            <select style={modalInputStyle} value={newUserEquipe} onChange={e => setNewUserEquipe(e.target.value)}>
                              <option value="">Equipe / Cooperativa (Obrigatório p/ Cooperativas)</option>
                              <optgroup label="Cooperativas Parceiras">
                                {cooperativas.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                              </optgroup>
                              <optgroup label="Equipes Internas">
                                {equipes.map(eq => <option key={eq.id} value={eq.name}>{eq.name}</option>)}
                              </optgroup>
                            </select>
                            <select style={modalInputStyle} value={newUserSetor} onChange={e => setNewUserSetor(e.target.value)}>
                              <option value="">Setor (Opcional)</option>
                              {setores.map(st => <option key={st.id} value={st.name}>{st.name}</option>)}
                            </select>
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', cursor: 'pointer', fontSize: '0.9rem', color: '#555' }}>
                            <input type="checkbox" checked={newUserForcePass} onChange={e => setNewUserForcePass(e.target.checked)} />
                            Obrigar troca de senha no primeiro acesso
                          </label>
                        </>
                      )}

                      {/* Campos somente para edição */}
                      {isEditing && (
                        <>
                          <h4 style={{ margin: '0 0 14px', color: '#444', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Identificação</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Nome na Agenda</label>
                              <input style={modalInputStyle} type="text" value={editUser?.agendaName || ''} onChange={e => handleUpdateAgendaName(modalUserId, e.target.value)} placeholder="Ex: Dr. Fulano" />
                            </div>
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Equipe / Cooperativa</label>
                              <select style={modalInputStyle} value={editUser?.equipe || ''} onChange={e => handleUpdateEquipe(modalUserId, e.target.value)}>
                                <option value="">Sem equipe / cooperativa</option>
                                <optgroup label="Cooperativas Parceiras">
                                  {cooperativas.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                                </optgroup>
                                <optgroup label="Equipes Internas">
                                  {equipes.map(eq => <option key={eq.id} value={eq.name}>{eq.name}</option>)}
                                </optgroup>
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Setor</label>
                              <select style={modalInputStyle} value={editUser?.setor || ''} onChange={e => handleUpdateSetor(modalUserId, e.target.value)}>
                                <option value="">Sem setor</option>
                                {setores.map(st => <option key={st.id} value={st.name}>{st.name}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Início no trabalho</label>
                              <input style={modalInputStyle} type="date" value={editUser?.inicioTrabalho || ''} onChange={e => handleUpdateInicioTrabalho(modalUserId, e.target.value)} />
                              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4, lineHeight: 1.4 }}>O INEP avisa o RH e o gerente da área se os cursos das turmas dele não estiverem feitos até essa data.</div>
                            </div>
                            {/* Troca de senha: agora / 30 / 60 / 90 / 120 dias / nunca */}
                            {(() => {
                              const sit = situacaoSenha(editUser);
                              const s = editUser?.senha || {};
                              const valorAtual = s.trocarAgora ? 'agora' : (s.validadeDias === undefined ? VALIDADE_PADRAO_DIAS : (s.validadeDias || 'nunca'));
                              const fmt = d => new Date(d).toLocaleDateString('pt-BR');
                              return (
                                <div>
                                  <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Troca de senha</label>
                                  <select style={{ ...modalInputStyle, borderColor: s.trocarAgora ? '#f59e0b' : undefined }} value={valorAtual} onChange={e => handleValidadeSenha(editUser, e.target.value)}>
                                    {OPCOES_VALIDADE.map(o => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
                                  </select>
                                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4, lineHeight: 1.4 }}>
                                    Última troca: {sit.trocadaEm ? fmt(sit.trocadaEm) : 'ainda não trocou pelo sistema novo'}
                                    {s.trocarAgora
                                      ? <span style={{ color: '#d97706', fontWeight: 600 }}> · troca pedida — sai em até 30 s</span>
                                      : sit.venceEm && <> · vence em <strong style={{ color: sit.vencida ? '#dc2626' : '#555' }}>{fmt(sit.venceEm)}</strong></>}
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Logout automático depois de 30 min parado — "Não" para logins de TV/painel */}
                            <div>
                              <label style={{ fontSize: '0.8rem', color: '#888', display: 'block', marginBottom: '4px' }}>Desconectar por inatividade</label>
                              <select style={{ ...modalInputStyle, borderColor: editUser?.semLogoutInatividade ? '#0ea5e9' : undefined }}
                                value={editUser?.semLogoutInatividade ? 'nao' : 'sim'}
                                onChange={e => updateDoc(doc(db, getCollectionName('users'), modalUserId), { semLogoutInatividade: e.target.value === 'nao' })}>
                                <option value="sim">Sim — sai depois de 30 min parado (padrão)</option>
                                <option value="nao">Não — fica sempre conectado (painel / TV)</option>
                              </select>
                              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 4, lineHeight: 1.4 }}>
                                Use "Não" só em logins de TV ou computador de painel, que ninguém usa para trabalhar.
                              </div>
                            </div>
                            {editUser?.bloqueado && (
                              <div style={{ gridColumn: 'span 2', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem' }}>
                                🚫 <strong>Acesso bloqueado</strong>{editUser.bloqueioEm ? ` em ${new Date(editUser.bloqueioEm).toLocaleString('pt-BR')}` : ''}{editUser.bloqueioPor ? ` por ${editUser.bloqueioPor}` : ''}. A pessoa não consegue entrar.
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Papéis */}
                      <h4 style={{ margin: '0 0 12px', color: '#444', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Papéis / Funções</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '20px', backgroundColor: '#f8f9fa', padding: '14px', borderRadius: '8px' }}>
                        {AVAILABLE_ROLES.map(role => {
                          const isChecked = isEditing ? (editUser?.roles || []).includes(role.id) : newUserRoles.includes(role.id);
                          return (
                            <label key={role.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85rem', color: '#444' }}>
                              <input type="checkbox" checked={isChecked} onChange={() => handleToggleRole(role.id, !isEditing, isEditing ? modalUserId : null)} />
                              {role.label}
                            </label>
                          );
                        })}
                      </div>

                      {/* Permissões */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: 8, flexWrap: 'wrap' }}>
                        <h4 style={{ margin: 0, color: '#444', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Permissões de Acesso</h4>
                        {!editUser?.soInep && (
                          <span style={{ display: 'flex', gap: 8 }}>
                            <button type="button" style={outlineBlueBtn} onClick={() => isEditing ? handleAuthorizeAllEdit(modalUserId) : handleAuthorizeAllNew()}>✅ Liberar tudo</button>
                            <button
                              type="button" style={{ ...outlineBlueBtn, color: '#dc2626', borderColor: '#fca5a5' }}
                              onClick={() => {
                                if (!window.confirm('Retirar TODOS os acessos deste usuário?')) return;
                                const vazio = AVAILABLE_MODULES.reduce((acc, m) => ({ ...acc, [m.id]: { view: false, edit: false } }), {});
                                if (isEditing) updateDoc(doc(db, getCollectionName('users'), modalUserId), { permissions: vazio });
                                else setNewUserPermissions(vazio);
                              }}
                            >✖ Retirar tudo</button>
                          </span>
                        )}
                      </div>
                      {editUser?.soInep && (
                        <p style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#7c3aed' }}>
                          Conta só INEP: outros módulos não funcionariam para ela (o banco bloqueia), por isso a liberação está travada.
                        </p>
                      )}
                      <div style={{ marginBottom: '24px' }}>
                        <PermissoesLiberacao
                          permissions={isEditing ? (editUser?.permissions || {}) : newUserPermissions}
                          disabled={!!editUser?.soInep}
                          onChange={novas => {
                            if (isEditing) updateDoc(doc(db, getCollectionName('users'), modalUserId), { permissions: novas });
                            else setNewUserPermissions(novas);
                          }}
                        />
                      </div>

                      {/* Ações do modal */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                              style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '9px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
                              onClick={() => handleDeleteUser(modalUserId)}
                            >🗑️ Remover</button>
                            {editUser?.bloqueado ? (
                              <button
                                style={{ background: 'none', border: '1px solid #16a34a', color: '#16a34a', padding: '9px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
                                onClick={() => handleBloquear(editUser, false)}
                              >✅ Desbloquear acesso</button>
                            ) : (
                              <button
                                style={{ background: 'none', border: '1px solid #64748b', color: '#475569', padding: '9px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
                                onClick={() => handleBloquear(editUser, true)}
                              >🚫 Bloquear acesso</button>
                            )}
                            <button
                              style={{ background: 'none', border: '1px solid #ffc107', color: '#d39e00', padding: '9px 18px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
                              onClick={() => handleResetPassword(editUser?.email)}
                            >🔑 Resetar Senha</button>
                          </div>
                        ) : <div />}
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button style={{ background: '#f0f0f0', border: 'none', padding: '9px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => setShowUserModal(false)}>Cancelar</button>
                          {!isEditing && (
                            <button
                              style={{ backgroundColor: '#007bff', color: '#fff', border: 'none', padding: '9px 24px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' }}
                              onClick={handleInviteUser}
                            >Criar Usuário</button>
                          )}
                          {isEditing && (
                            <button
                              style={{ backgroundColor: '#6c757d', color: '#fff', border: 'none', padding: '9px 24px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' }}
                              onClick={() => setShowUserModal(false)}
                            >Salvar e Implantar</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* CABEÇALHO: BUSCA + BOTÃO NOVO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h3 style={{ margin: 0, color: '#1a1a2e' }}>Usuários Cadastrados</h3>
                <span style={{ backgroundColor: '#007bff', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: '600' }}>{users.length}</span>
                <ContadorEmails compacto />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="🔍 Pesquisar por nome, e-mail ou papel..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', minWidth: '260px', outline: 'none' }}
                />
                <button
                  style={{ backgroundColor: '#6366f1', color: '#fff', border: 'none', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => setShowWizard(true)}
                >➕ Novo Usuário</button>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}><ContadorEmails soAviso /></div>

            {/* TABELA DE USUÁRIOS */}
            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              {/* Cabeçalho da tabela */}
              <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 2fr 2fr 1fr 100px', backgroundColor: '#f0f4ff', padding: '10px 16px', fontWeight: '700', fontSize: '0.78rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e7ff' }}>
                <span>Usuário</span>
                <span>E-mail</span>
                <span>Papéis</span>
                <span style={{ textAlign: 'center' }}>Status</span>
                <span style={{ textAlign: 'center' }}>Ações</span>
              </div>
              {/* Linhas */}
              {users
                .filter(u => {
                  if (!userSearch.trim()) return true;
                  const q = userSearch.toLowerCase();
                  return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.agendaName || '').toLowerCase().includes(q) || (u.equipe || '').toLowerCase().includes(q) || ((u.roles || []).join(' ').toLowerCase()).includes(q);
                })
                .map((user, idx) => {
                  const initials = (user.name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
                  const palette = ['#007bff', '#28a745', '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#17a2b8'];
                  const color = palette[(user.name || '').charCodeAt(0) % palette.length] || '#007bff';
                  return (
                    <div
                      key={user.id}
                      style={{ display: 'grid', gridTemplateColumns: '2.5fr 2fr 2fr 1fr 100px', padding: '12px 16px', alignItems: 'center', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbff', borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f4ff'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#fafbff'}
                    >
                      {/* Col 1: Avatar + Nome */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.9rem', flexShrink: 0 }}>{initials}</div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1a1a2e', fontSize: '0.95rem' }}>
                            {user.name}
                            {(() => { const inep = situacaoInep(user, inepAdmins); return inep && <span style={{ marginLeft: 6, background: inep.fundo, color: inep.cor, padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{inep.tipo === 'so_inep' ? '🎓 Aluno INEP' : inep.rotulo}</span>; })()}
                          </div>
                          {user.agendaName && user.agendaName !== user.name && <div style={{ fontSize: '0.75rem', color: '#888' }}>Agenda: {user.agendaName}</div>}
                          {user.equipe && <div style={{ fontSize: '0.75rem', color: '#6f42c1', fontWeight: '500' }}>Equipe: {user.equipe}</div>}
                        </div>
                      </div>
                      {/* Col 2: E-mail */}
                      <div style={{ fontSize: '0.85rem', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                      {/* Col 3: Papéis */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(user.roles || []).length === 0
                          ? <span style={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>sem papel</span>
                          : (user.roles || []).map(rid => {
                            const ri = AVAILABLE_ROLES.find(r => r.id === rid);
                            return ri ? <span key={rid} style={{ backgroundColor: '#e7f1ff', color: '#0056b3', padding: '2px 8px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: '600' }}>{ri.label}</span> : null;
                          })
                        }
                      </div>
                      {/* Col 4: Status */}
                      <div style={{ textAlign: 'center' }}>
                        {user.bloqueado
                          ? <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: '600' }}>Bloqueado</span>
                          : (user.forcePasswordChange || situacaoSenha(user).vencida)
                            ? <span style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '3px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: '600' }}>Trocar Senha</span>
                            : <span style={{ backgroundColor: '#d4edda', color: '#155724', padding: '3px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: '600' }}>Ativo</span>
                        }
                      </div>
                      {/* Col 5: Ações */}
                      <div style={{ textAlign: 'center' }}>
                        <button
                          style={{ backgroundColor: '#007bff', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' }}
                          onClick={() => { setModalUserId(user.id); setError(''); setEditUserMode(user.id); setShowUserModal(true); }}
                        >Editar</button>
                      </div>
                    </div>
                  );
                })
              }
              {users.filter(u => {
                if (!userSearch.trim()) return true;
                const q = userSearch.toLowerCase();
                return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.agendaName || '').toLowerCase().includes(q) || (u.equipe || '').toLowerCase().includes(q) || ((u.roles || []).join(' ')).toLowerCase().includes(q);
              }).length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '0.95rem' }}>
                    {userSearch ? `Nenhum usuário encontrado para "${userSearch}"` : 'Nenhum usuário cadastrado.'}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: PERFIS DE ACESSO                                        */}
        {/* ============================================================ */}
        {activeTab === 'perfis' && (
          <PerfilConfig users={users} />
        )}

        {/* ============================================================ */}
        {/* ABA: AUDITORES QUALIDADE                                     */}
        {/* ============================================================ */}
        {activeTab === 'auditors' && (
          <div className="tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Auditores Qualidade</h3>
                <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.85rem' }}>Vincule cada auditor a um login do sistema para ligar notificações e filtros automaticamente.</p>
              </div>
            </div>

            <form onSubmit={handleAddAuditor} className="simple-add-form">
              <input type="text" placeholder="Nome do Auditor" value={newAuditorName} onChange={e => setNewAuditorName(e.target.value)} required />
              <input type="color" value={newAuditorColor} onChange={e => setNewAuditorColor(e.target.value)} title="Cor da etiqueta" />
              <button type="submit">Adicionar Auditor</button>
            </form>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
              {auditors.map(auditor => {
                const isLinked = !!auditor.linkedUserId;
                return (
                  <div key={auditor.id} style={{
                    display: 'grid', gridTemplateColumns: '18px 1fr 2.5fr auto',
                    alignItems: 'center', gap: '16px',
                    backgroundColor: '#fff', borderRadius: '10px',
                    padding: '14px 18px',
                    border: `1px solid ${isLinked ? '#c3e6cb' : '#e9ecef'}`,
                    borderLeft: `5px solid ${auditor.color}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    {/* Indicador de status */}
                    <div title={isLinked ? 'Vinculado' : 'Sem vínculo'} style={{
                      width: '12px', height: '12px', borderRadius: '50%',
                      backgroundColor: isLinked ? '#28a745' : '#dee2e6',
                      flexShrink: 0
                    }} />

                    {/* Nome do auditor */}
                    <div>
                      <div style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '1rem' }}>{auditor.name}</div>
                      {isLinked
                        ? <div style={{ fontSize: '0.75rem', color: '#28a745', marginTop: '2px' }}>✔ {auditor.linkedUserEmail}</div>
                        : <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '2px', fontStyle: 'italic' }}>Sem usuário vinculado</div>
                      }
                    </div>

                    {/* Seletor de usuário */}
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Vincular ao login:</label>
                      <select
                        value={auditor.linkedUserId || ''}
                        onChange={e => handleLinkAuditorUser(auditor.id, e.target.value || null)}
                        style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: `1px solid ${isLinked ? '#28a745' : '#ddd'}`, fontSize: '0.88rem', backgroundColor: isLinked ? '#f0fff4' : '#fff', color: '#333', cursor: 'pointer', outline: 'none' }}
                      >
                        <option value="">— Selecionar usuário —</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>

                    {/* Excluir */}
                    {confirmDeleteAuditorId === auditor.id ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleDeleteAuditor(auditor.id)}
                          style={{ background: '#dc3545', border: 'none', color: '#fff', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}
                        >Confirmar</button>
                        <button
                          onClick={() => setConfirmDeleteAuditorId(null)}
                          style={{ background: '#e9ecef', border: 'none', color: '#555', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem' }}
                        >Cancelar</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteAuditorId(auditor.id)}
                        style={{ background: 'none', border: '1px solid #dee2e6', color: '#aaa', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '1rem', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc3545'; e.currentTarget.style.color = '#dc3545'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.color = '#aaa'; }}
                      >🗑️</button>
                    )}
                  </div>
                );
              })}
            </div>

            {auditors.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhum auditor cadastrado.</p>
                <button className="btn-save" onClick={seedAuditores}>Restaurar Padrões (Seed)</button>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: AUDITORES HOMECARE                                      */}
        {/* ============================================================ */}
        {activeTab === 'homecare_auditors' && (
          <div className="tab-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0 }}>Auditores Homecare</h3>
                <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.85rem' }}>Vincule cada auditor a um login do sistema para ligar notificações e filtros automaticamente.</p>
              </div>
            </div>

            <form onSubmit={handleAddHomecareAuditor} className="simple-add-form">
              <input type="text" placeholder="Nome do Auditor" value={newHomecareAuditorName} onChange={e => setNewHomecareAuditorName(e.target.value)} required />
              <input type="color" value={newHomecareAuditorColor} onChange={e => setNewHomecareAuditorColor(e.target.value)} title="Cor da etiqueta" />
              <button type="submit">Adicionar Auditor</button>
            </form>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
              {homecareAuditors.map(auditor => {
                const isLinked = !!auditor.linkedUserId;
                return (
                  <div key={auditor.id} style={{
                    display: 'grid', gridTemplateColumns: '18px 1fr 2.5fr auto',
                    alignItems: 'center', gap: '16px',
                    backgroundColor: '#fff', borderRadius: '10px',
                    padding: '14px 18px',
                    border: `1px solid ${isLinked ? '#c3e6cb' : '#e9ecef'}`,
                    borderLeft: `5px solid ${auditor.color}`,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                  }}>
                    <div title={isLinked ? 'Vinculado' : 'Sem vínculo'} style={{
                      width: '12px', height: '12px', borderRadius: '50%',
                      backgroundColor: isLinked ? '#28a745' : '#dee2e6', flexShrink: 0
                    }} />
                    <div>
                      <div style={{ fontWeight: '700', color: '#1a1a2e', fontSize: '1rem' }}>{auditor.name}</div>
                      {isLinked
                        ? <div style={{ fontSize: '0.75rem', color: '#28a745', marginTop: '2px' }}>✔ {auditor.linkedUserEmail}</div>
                        : <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '2px', fontStyle: 'italic' }}>Sem usuário vinculado</div>
                      }
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Vincular ao login:</label>
                      <select
                        value={auditor.linkedUserId || ''}
                        onChange={e => handleLinkHomecareAuditorUser(auditor.id, e.target.value || null)}
                        style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: `1px solid ${isLinked ? '#28a745' : '#ddd'}`, fontSize: '0.88rem', backgroundColor: isLinked ? '#f0fff4' : '#fff', color: '#333', cursor: 'pointer', outline: 'none' }}
                      >
                        <option value="">— Selecionar usuário —</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>
                    {confirmDeleteHomecareAuditorId === auditor.id ? (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleDeleteHomecareAuditor(auditor.id)}
                          style={{ background: '#dc3545', border: 'none', color: '#fff', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}
                        >Confirmar</button>
                        <button
                          onClick={() => setConfirmDeleteHomecareAuditorId(null)}
                          style={{ background: '#e9ecef', border: 'none', color: '#555', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem' }}
                        >Cancelar</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteHomecareAuditorId(auditor.id)}
                        style={{ background: 'none', border: '1px solid #dee2e6', color: '#aaa', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '1rem' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc3545'; e.currentTarget.style.color = '#dc3545'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.color = '#aaa'; }}
                      >🗑️</button>
                    )}
                  </div>
                );
              })}
            </div>

            {homecareAuditors.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhum auditor cadastrado para Homecare.</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: TAREFAS HOMECARE CONFIG                                 */}
        {/* ============================================================ */}
        {activeTab === 'homecare_tarefas' && (
          <div className="tab-content">
            <h3 style={{ margin: '0 0 20px 0' }}>Configurações de Tarefas Homecare</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              
              {/* BLOCO 1: SLA */}
              <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>⏳ SLAs Padrão (em horas)</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#c62828', fontWeight: 'bold' }}>Urgente e Importante</span>
                    <input type="number" min="1" value={homecareTarefasConfig.slas.urgente_importante} onChange={e => setHomecareTarefasConfig(prev => ({...prev, slas: {...prev.slas, urgente_importante: parseInt(e.target.value, 10)}}))} style={{ width: '80px', padding: '5px' }} />
                  </label>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#fbc02d', fontWeight: 'bold' }}>Não Urgente e Importante</span>
                    <input type="number" min="1" value={homecareTarefasConfig.slas.nao_urgente_importante} onChange={e => setHomecareTarefasConfig(prev => ({...prev, slas: {...prev.slas, nao_urgente_importante: parseInt(e.target.value, 10)}}))} style={{ width: '80px', padding: '5px' }} />
                  </label>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#f57c00', fontWeight: 'bold' }}>Urgente e Não Importante</span>
                    <input type="number" min="1" value={homecareTarefasConfig.slas.urgente_nao_importante} onChange={e => setHomecareTarefasConfig(prev => ({...prev, slas: {...prev.slas, urgente_nao_importante: parseInt(e.target.value, 10)}}))} style={{ width: '80px', padding: '5px' }} />
                  </label>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>Não Urg. e Não Importante</span>
                    <input type="number" min="1" value={homecareTarefasConfig.slas.nao_urgente_nao_importante} onChange={e => setHomecareTarefasConfig(prev => ({...prev, slas: {...prev.slas, nao_urgente_nao_importante: parseInt(e.target.value, 10)}}))} style={{ width: '80px', padding: '5px' }} />
                  </label>
                  <button onClick={handleSaveHomecareSlas} style={{ marginTop: '10px', padding: '8px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>Salvar SLAs</button>
                </div>
              </div>

              {/* BLOCO 2: SETORES */}
              <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>🏥 Setores do Homecare</h4>
                <form onSubmit={handleAddHomecareSetor} style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <input type="text" placeholder="Nome do Setor" value={newHomecareSetor} onChange={e => setNewHomecareSetor(e.target.value)} required style={{ flexGrow: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }} />
                  <button type="submit" style={{ padding: '8px 15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add</button>
                </form>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(homecareTarefasConfig.setores || []).map(setor => (
                    <div key={setor} style={{ background: '#f8f9fa', padding: '5px 10px', borderRadius: '15px', border: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                      {setor}
                      <button onClick={() => handleRemoveHomecareSetor(setor)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: 0 }}>×</button>
                    </div>
                  ))}
                  {!(homecareTarefasConfig.setores || []).length && <span style={{ color: '#888', fontSize: '0.85rem' }}>Nenhum setor cadastrado.</span>}
                </div>
              </div>

            </div>

            {/* BLOCO 3: EXECUTANTES */}
            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginTop: '20px' }}>
              <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>👥 Executantes (Vincular Usuários do Sistema)</h4>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '15px' }}>Selecione abaixo quais usuários devem aparecer na lista de Executantes ao criar uma tarefa Homecare.</p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px', maxHeight: '400px', overflowY: 'auto', padding: '10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee' }}>
                {users.map(u => {
                  const isExec = (homecareTarefasConfig.executantes || []).includes(u.id);
                  return (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: isExec ? '#e8f4f8' : '#fff', borderRadius: '6px', border: `1px solid ${isExec ? '#17a2b8' : '#ddd'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                      <input 
                        type="checkbox" 
                        checked={isExec}
                        onChange={() => handleToggleHomecareExecutante(u.id)}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: isExec ? 'bold' : 'normal', fontSize: '0.9rem' }}>{u.name}</span>
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>{u.email}</span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: COOPERATIVAS                                            */}
        {/* ============================================================ */}
        {activeTab === 'cooperativas' && (
          <div className="tab-content">
            <h3>Gerenciar Cooperativas Parceiras</h3>
            <form onSubmit={handleAddCooperativa} className="simple-add-form">
              <input type="text" placeholder="Nome da Cooperativa (Ex: Prontobaby)" value={newCooperativaName} onChange={e => setNewCooperativaName(e.target.value)} required />
              <button type="submit">Adicionar Cooperativa</button>
            </form>
            <div className="card-grid">
              {cooperativas.map(coop => (
                <div key={coop.id} className="simple-card" style={{ borderLeft: `5px solid #007bff` }}>
                  <span>{coop.name}</span>
                  <button className="btn-icon-delete" onClick={() => handleDeleteCooperativa(coop.id)}>🗑️</button>
                </div>
              ))}
            </div>
            {cooperativas.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhuma cooperativa cadastrada.</p>
                <button className="btn-save" onClick={seedCooperativas}>Restaurar Padrões (Seed)</button>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: EMPRESAS TERCEIRAS                                       */}
        {/* ============================================================ */}
        {activeTab === 'empresas' && (
          <div className="tab-content">
            <h3>Gerenciar Empresas Terceiras</h3>
            <form onSubmit={handleAddEmpresaTerceira} className="simple-add-form">
              <input type="text" placeholder="Nome da Empresa Terceira (Ex: Quality)" value={newEmpresaTerceiraName} onChange={e => setNewEmpresaTerceiraName(e.target.value)} required />
              <button type="submit">Adicionar Empresa</button>
            </form>
            <div className="card-grid">
              {empresasTerceiras.map(emp => (
                <div key={emp.id} className="simple-card" style={{ borderLeft: `5px solid #e67e22` }}>
                  <span>{emp.name}</span>
                  <button className="btn-icon-delete" onClick={() => handleDeleteEmpresaTerceira(emp.id)}>🗑️</button>
                </div>
              ))}
            </div>
            {empresasTerceiras.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhuma empresa terceira cadastrada.</p>
                <button className="btn-save" onClick={seedEmpresasTerceiras}>Restaurar Padrões (Seed)</button>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: UNIDADES                                                */}
        {/* ============================================================ */}
        {activeTab === 'units' && (
          <div className="tab-content">
            <h3>Gerenciar Unidades</h3>
            <form onSubmit={handleAddUnit} className="simple-add-form">
              <input type="text" placeholder="Nome da Unidade" value={newUnitName} onChange={e => setNewUnitName(e.target.value)} required />
              <input type="color" value={newUnitColor} onChange={e => setNewUnitColor(e.target.value)} title="Cor da etiqueta" />
              <button type="submit">Adicionar Unidade</button>
            </form>
            <div className="card-grid">
              {units.map(unit => (
                <div key={unit.id} className="simple-card" style={{ borderLeft: `5px solid ${unit.color}` }}>
                  <span>{unit.name}</span>
                  <button className="btn-icon-delete" onClick={() => handleDeleteUnit(unit.id)}>🗑️</button>
                </div>
              ))}
            </div>
            {units.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhuma unidade cadastrada.</p>
                <button className="btn-save" onClick={seedUnidades}>Restaurar Padrões (Seed)</button>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: EQUIPES                                                 */}
        {/* ============================================================ */}
        {activeTab === 'equipes' && (
          <div className="tab-content">
            <h3>Gerenciar Equipes</h3>
            <form onSubmit={handleAddEquipe} className="simple-add-form">
              <input type="text" placeholder="Nome da Equipe" value={newEquipeName} onChange={e => setNewEquipeName(e.target.value)} required />
              <input type="color" value={newEquipeColor} onChange={e => setNewEquipeColor(e.target.value)} title="Cor da etiqueta" />
              <button type="submit">Adicionar Equipe</button>
            </form>
            <div className="card-grid">
              {equipes.map(eq => (
                <div key={eq.id} className="simple-card" style={{ borderLeft: `5px solid ${eq.color}` }}>
                  <span>{eq.name}</span>
                  <button className="btn-icon-delete" onClick={() => handleDeleteEquipe(eq.id)}>🗑️</button>
                </div>
              ))}
            </div>
            {equipes.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhuma equipe cadastrada.</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: SETORES                                                  */}
        {/* ============================================================ */}
        {activeTab === 'setores' && (
          <div className="tab-content">
            <h3>🏢 Gerenciar Setores</h3>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '15px' }}>Setores são usados para vincular usuários às suas ocorrências e para controle de estoque. Cada usuário pode ter um setor associado no seu perfil.</p>
            <form onSubmit={handleAddSetor} className="simple-add-form">
              <input type="text" placeholder="Nome do Setor (Ex: TI, RH, Enfermagem)" value={newSetorName} onChange={e => setNewSetorName(e.target.value)} required />
              <button type="submit">Adicionar Setor</button>
            </form>
            <div className="card-grid">
              {setores.map(st => (
                <div key={st.id} className="simple-card">
                  <span>{st.name}</span>
                  <button className="btn-icon-delete" onClick={() => handleDeleteSetor(st.id)}>🗑️</button>
                </div>
              ))}
            </div>
            {setores.length === 0 && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <p className="empty-msg">Nenhum setor cadastrado.</p>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: CAPTAÇÃO CONFIG                                         */}
        {/* ============================================================ */}
        {activeTab === 'captacao_config' && (
          <div className="tab-content">
            <h3 style={{ margin: '0 0 20px' }}>🏥 Configurações da Captação de Pacientes</h3>

            {/* SLA */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
              <div style={{ background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 16px', borderBottom: '1px solid #eee', paddingBottom: 10 }}>⏳ SLA Padrão da Captação</h4>
                <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 16 }}>
                  Tempo máximo (em horas) antes do SLA ser considerado crítico no Kanban.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 'bold', color: '#1e293b' }}>Paciente Padrão</span>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Homebaby / Vitalcare sem VM</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" min="1" max="720" value={captacaoConfig.sla_padrao}
                        onChange={e => setCaptacaoConfig(prev => ({ ...prev, sla_padrao: parseInt(e.target.value, 10) || 24 }))}
                        style={{ width: 70, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, textAlign: 'center' }} />
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>h</span>
                    </div>
                  </label>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 'bold', color: '#1e293b' }}>Paciente VM</span>
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>Com Ventilação Mecânica</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" min="1" max="720" value={captacaoConfig.sla_vm}
                        onChange={e => setCaptacaoConfig(prev => ({ ...prev, sla_vm: parseInt(e.target.value, 10) || 48 }))}
                        style={{ width: 70, padding: '5px 8px', border: '1px solid #ddd', borderRadius: 6, textAlign: 'center' }} />
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>h</span>
                    </div>
                  </label>
                </div>
                <button onClick={handleSaveCaptacaoSla} disabled={savingCaptacaoSla}
                  style={{ marginTop: 16, padding: '9px 20px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, opacity: savingCaptacaoSla ? 0.7 : 1 }}>
                  {savingCaptacaoSla ? '⏳ Salvando...' : '✅ Salvar SLA'}
                </button>
              </div>

              <div style={{ background: '#f0fdf4', padding: 20, borderRadius: 10, border: '1px solid #bbf7d0' }}>
                <h4 style={{ margin: '0 0 12px', color: '#166534' }}>ℹ️ Como funciona o SLA</h4>
                <p style={{ fontSize: '0.85rem', color: '#15803d', lineHeight: 1.7, margin: 0 }}>
                  O SLA é contado a partir da data de abertura do registro.<br /><br />
                  <strong>Verde</strong> — menos de 50% do tempo consumido<br />
                  <strong>Amarelo</strong> — entre 50% e 80%<br />
                  <strong>Vermelho</strong> — acima de 80% (crítico)<br /><br />
                  A pausa do SLA por etapa pode ser configurada em versão futura.
                </p>
              </div>
            </div>

            {/* Profissionais e Convênios */}
            <div style={{ background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <h4 style={{ margin: '0 0 16px', borderBottom: '1px solid #eee', paddingBottom: 10 }}>👥 Profissionais e Convênios</h4>
              <CaptacaoConfigPanel />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: ESPECIALIDADES                                          */}
        {/* ============================================================ */}
        {activeTab === 'especialidades' && (
          <div className="tab-content">
            <h3 style={{ margin: '0 0 6px' }}>🩺 Especialidades / Tipos de Profissional</h3>
            <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: 20 }}>
              Lista usada no Portal da Cooperativa e Avaliação RH ao cadastrar profissionais.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Formulário de adição */}
              <div style={{ background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 14px', borderBottom: '1px solid #eee', paddingBottom: 10 }}>➕ Nova Especialidade</h4>
                <form onSubmit={handleAddEspecialidade} style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Ex: TERAPEUTA OCUPACIONAL"
                    value={novaEspecialidade}
                    onChange={e => setNovaEspecialidade(e.target.value)}
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 7, fontSize: '0.88rem', textTransform: 'uppercase' }}
                  />
                  <button type="submit" style={{ padding: '9px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700 }}>
                    Adicionar
                  </button>
                </form>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 10 }}>
                  O nome será salvo em maiúsculas automaticamente.
                </p>
              </div>

              {/* Lista atual */}
              <div style={{ background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e9ecef', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 14px', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
                  📋 Especialidades cadastradas ({especialidades.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                  {especialidades.map(esp => (
                    <div key={esp} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{esp}</span>
                      <button
                        onClick={() => handleRemoveEspecialidade(esp)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '1rem', lineHeight: 1, padding: '0 4px' }}
                        title="Remover">
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: LIXEIRA CAPTAÇÃO                                        */}
        {/* ============================================================ */}
        {activeTab === 'captacao_lixeira' && (
          <div className="tab-content">
            <h3 style={{ margin: '0 0 20px' }}>🗑️ Lixeira Captação de Pacientes</h3>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '20px' }}>Pacientes excluídos da Captação. Você pode restaurá-lo a qualquer momento.</p>
            {captacaoLixeira.length === 0 ? (
              <p style={{ color: '#aaa' }}>Nenhum paciente na lixeira.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee', textAlign: 'left', color: '#666' }}>
                    <th style={{ padding: '10px 8px' }}>Paciente</th>
                    <th style={{ padding: '10px 8px' }}>Excluído Por</th>
                    <th style={{ padding: '10px 8px' }}>Data Exclusão</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {captacaoLixeira.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 8px', fontWeight: 'bold' }}>{p.nome}</td>
                      <td style={{ padding: '10px 8px' }}>{p.excluidoPor || 'Desconhecido'}</td>
                      <td style={{ padding: '10px 8px' }}>{p.excluidoEm ? new Date(p.excluidoEm).toLocaleString() : ''}</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                        <button 
                          onClick={async () => {
                            if (!window.confirm(`Restaurar o paciente ${p.nome}?`)) return;
                            try {
                              await updateDoc(doc(db, getCollectionName('captacao_pacientes'), p.id), {
                                excluido: false,
                                excluidoEm: null,
                                excluidoPor: null
                              });
                            } catch(e) { alert('Erro ao restaurar: ' + e.message); }
                          }}
                          style={{ padding: '6px 12px', background: '#ecfdf5', color: '#10b981', border: '1px solid #a7f3d0', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                          Restaurar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'voz' && (
          <div className="tab-content" style={{ padding:0 }}>
            <SpeechConfig />
          </div>
        )}

        {activeTab === 'templates_iw' && currentUser?.email === 'alexandrebareaoliveira@gmail.com' && <TemplatesIW />}

        {activeTab === 'servidor' && currentUser?.email === 'alexandrebareaoliveira@gmail.com' && <ServidorStatus />}

        {activeTab === 'tabelas_hc' && currentUser?.email === 'alexandrebareaoliveira@gmail.com' && (
          <div className="tab-content" style={{ padding: 0 }}>
            <TabelasHCAdmin />
          </div>
        )}

        {/* ============================================================ */}
        {/* ABA: USUÁRIOS ONLINE                                         */}
        {/* ============================================================ */}
        {activeTab === 'online_users' && (
          <OnlineUsers />
        )}

        {/* Checklist presencial de uso do Quality por colaborador */}
        {activeTab === 'auditoria_uso' && <AuditoriaUso users={users} />}

        {activeTab === 'maintenance' && (
          <div className="tab-content" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px', color: '#1a1a2e' }}>Manutenção do Sistema</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              <div style={{ background: manutencaoAtivo ? '#fef2f2' : '#fff', padding: '24px', borderRadius: '12px', border: manutencaoAtivo ? '1px solid #fecaca' : '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 12px', color: '#374151', fontSize: '1rem' }}>
                  🛠️ Bloqueio de Acesso (troca de servidor)
                </h4>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '12px' }}>
                  Quando ligado, ninguém (exceto você) consegue usar o sistema — todos veem uma tela de "em manutenção". Use durante a janela de troca de servidor da migração (Fase 8 do roteiro).
                </p>
                <textarea
                  value={manutencaoMsg}
                  onChange={(e) => setManutencaoMsg(e.target.value)}
                  placeholder="Mensagem mostrada pra quem tentar acessar..."
                  rows={2}
                  style={{ width: '100%', marginBottom: '12px', padding: '8px', borderRadius: '8px', border: '1px solid #d1d5db', fontFamily: 'inherit', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
                <button
                  onClick={handleToggleManutencao}
                  disabled={manutencaoSalvando}
                  style={{
                    width: '100%', background: manutencaoAtivo ? '#dc2626' : '#16a34a', color: '#fff', border: 'none',
                    padding: '12px', borderRadius: '8px', cursor: manutencaoSalvando ? 'not-allowed' : 'pointer', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: manutencaoSalvando ? 0.7 : 1,
                  }}
                >
                  {manutencaoSalvando ? 'Salvando...' : manutencaoAtivo ? '🔴 Desativar manutenção (liberar sistema)' : '🟢 Ativar manutenção (bloquear acesso)'}
                </button>
              </div>
              <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <h4 style={{ margin: '0 0 12px', color: '#374151', fontSize: '1rem' }}>Limpeza de Dados</h4>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '20px' }}>
                  Esta ferramenta percorre todos os pacientes e profissionais cadastrados e converte os nomes para o formato correto (Ex: "JOÃO DA SILVA" → "João da Silva").
                </p>
                <button 
                  onClick={handleMigrateNames}
                  style={{ 
                    width: '100%', background: '#4f46e5', color: '#fff', border: 'none', 
                    padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' 
                  }}
                >
                  ✨ Padronizar Todos os Nomes
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Settings;